import { useEffect, useRef, useState } from "react";
import { devLog } from "../../config/devLog.js";
import { ADSENSE_CLIENT, ADSENSE_TEST_MODE, isAdSenseHostAllowed } from "../../config/adsense.js";
import "./AdUnit.css";

export default function AdUnit({
  slot,
  format = "auto",
  className = "",
  style,
  fullWidthResponsive = "true",
  layout,
  layoutKey,
  collapseIfEmpty = false,
  onSlotStateChange,
}) {
  const adRef = useRef(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (!isAdSenseHostAllowed()) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      devLog("AdUnit", e?.message);
    }
  }, [slot, format, layout, layoutKey]);

  useEffect(() => {
    if (!collapseIfEmpty || !isAdSenseHostAllowed()) return;
    setIsCollapsed(false);
    const node = adRef.current;
    if (!node) return undefined;

    const evaluateSlotState = () => {
      if (!node) return;
      const adStatus = node.getAttribute("data-ad-status");
      if (adStatus === "unfilled") {
        setIsCollapsed(true);
        if (typeof onSlotStateChange === "function") onSlotStateChange("unfilled");
        return;
      }
      if (adStatus === "filled") {
        setIsCollapsed(false);
        if (typeof onSlotStateChange === "function") onSlotStateChange("filled");
      }
    };

    // If AdSense does not fill, collapse this slot to avoid large blank placeholders.
    const timeoutId = window.setTimeout(() => {
      evaluateSlotState();
      if (node.getAttribute("data-ad-status") === "unfilled") return;
      const iframe = node.querySelector("iframe");
      if (!iframe) {
        setIsCollapsed(true);
        if (typeof onSlotStateChange === "function") onSlotStateChange("unfilled");
        return;
      }
      const frameRect = iframe.getBoundingClientRect();
      const isFrameVisible = frameRect.width > 20 && frameRect.height > 20;
      if (!isFrameVisible) {
        setIsCollapsed(true);
        if (typeof onSlotStateChange === "function") onSlotStateChange("unfilled");
      } else if (typeof onSlotStateChange === "function") {
        onSlotStateChange("filled");
      }
    }, 4500);

    const observer = new MutationObserver(() => {
      evaluateSlotState();
    });
    observer.observe(node, {
      attributes: true,
      attributeFilter: ["data-ad-status"],
      childList: true,
      subtree: true,
    });

    return () => {
      window.clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [collapseIfEmpty, slot, format, layout, layoutKey, onSlotStateChange]);

  if (!isAdSenseHostAllowed() || !slot) return null;
  if (isCollapsed) return null;

  return (
    <ins
      ref={adRef}
      className={`adsbygoogle ad-slot ${className}`.trim()}
      style={style || { display: "block" }}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={slot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
      data-ad-layout={layout}
      data-ad-layout-key={layoutKey}
      data-adtest={ADSENSE_TEST_MODE ? "on" : undefined}
    />
  );
}
